import { Component, OnInit } from '@angular/core';
import { Title } from '@angular/platform-browser';

import { TreasuryRate, TreasuryService } from '../../core/treasury.service';

/** An AsOfDate choice in the filter dropdown. */
interface DateOption {
  label: string;
  value: string;
}

/**
 * Displays the stored US Treasury yield-curve rates in a table.
 *
 * The user filters by AsOfDate; the dropdown lists the available dates newest
 * first and defaults to the latest one, so the page opens on the most recent
 * curve.
 */
@Component({
  selector: 'app-treasury-rates',
  standalone: false,
  templateUrl: './treasury-rates.component.html',
  styleUrl: './treasury-rates.component.scss'
})
export class TreasuryRatesComponent implements OnInit {
  /** Every row returned by the API (already newest-first from the server). */
  private allRows: TreasuryRate[] = [];

  dateOptions: DateOption[] = [];
  selectedDate = '';

  loading = false;
  error: string | null = null;

  constructor(
    private readonly treasury: TreasuryService,
    private readonly title: Title
  ) {}

  ngOnInit(): void {
    this.title.setTitle('US Treasury Rates');
    this.load();
  }

  /** Loads all rates and defaults the filter to the latest AsOfDate. */
  load(): void {
    this.loading = true;
    this.error = null;
    this.treasury.getTreasuryRates().subscribe({
      next: (rows) => {
        this.allRows = rows;

        // Distinct AsOfDates, newest first. ISO yyyy-MM-dd sorts chronologically
        // as plain strings, so a reverse lexical sort gives descending dates.
        const dates = Array.from(
          new Set(rows.map((r) => r.asofdate).filter((d): d is string => !!d))
        ).sort((a, b) => b.localeCompare(a));

        this.dateOptions = dates.map((d) => ({ label: formatDate(d), value: d }));

        // On load, show the latest AsOfDate.
        this.selectedDate = dates[0] ?? '';
        this.loading = false;
      },
      error: () => {
        this.allRows = [];
        this.dateOptions = [];
        this.selectedDate = '';
        this.loading = false;
        this.error = 'Could not load Treasury rates. Please try again.';
      }
    });
  }

  /** Rows for the selected AsOfDate, ordered by term (months) ascending. */
  get rows(): TreasuryRate[] {
    return this.allRows
      .filter((r) => r.asofdate === this.selectedDate)
      .sort((a, b) => (a.months ?? 0) - (b.months ?? 0));
  }

  /** Label for the currently selected date. */
  get selectedDateLabel(): string {
    return this.selectedDate ? formatDate(this.selectedDate) : '';
  }

  /** Formats a term in months as a readable label (e.g. 6 → "6 months", 24 → "2 years"). */
  formatMonths(months: number | null): string {
    if (months == null) {
      return '';
    }
    if (months % 12 === 0) {
      const years = months / 12;
      return `${years} year${years === 1 ? '' : 's'}`;
    }
    return `${months} month${months === 1 ? '' : 's'}`;
  }

  /** Formats a yield as a percentage (e.g. 4.55 → "4.55%"). */
  formatRate(rate: number | null): string {
    if (rate == null) {
      return '';
    }
    return `${rate.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 3
    })}%`;
  }
}

/** Formats an ISO `yyyy-MM-dd` date as e.g. "Aug 14, 2026". */
function formatDate(iso: string): string {
  // Parse as local midnight so display doesn't shift a day in negative time zones.
  const date = new Date(`${iso}T00:00:00`);
  return isNaN(date.getTime())
    ? iso
    : date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}
